/**
 * Downloaded episode audio kept on the device (IndexedDB), so an episode is fetched once and then
 * opens instantly — also after the app is closed, and on any server address.
 * Keyed by episode and variant, not by the signed link (which changes daily). Least recently used
 * entries are dropped past MAX_ENTRIES / MAX_BYTES; entries older than MAX_AGE are refetched.
 */
const DB = 'engpod-media';
const STORE = 'files';
const MAX_ENTRIES = 8;
const MAX_BYTES = 200 * 1024 * 1024;
const MAX_AGE = 30 * 86_400_000;

interface Entry {
  key: string;
  blob: Blob;
  savedAt: number;
  usedAt: number;
}

/** `/api/media/<id>?u=…&exp=…&sig=…&variant=audio` → `media/<id>/audio` (null for other URLs). */
export function mediaCacheKey(url: string): string | null {
  try {
    const u = new URL(url, typeof location === 'undefined' ? 'http://x' : location.href);
    const m = u.pathname.match(/\/api\/media\/([^/]+)$/);
    return m ? `media/${m[1]}/${u.searchParams.get('variant') ?? 'main'}` : null;
  } catch {
    return null;
  }
}

let opening: Promise<IDBDatabase | null> | null = null;
function open(): Promise<IDBDatabase | null> {
  opening ??= new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'key' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  return opening;
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(undefined);
        const tx = db.transaction(STORE, mode);
        const req = fn(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(req ? req.result : undefined);
        tx.onerror = tx.onabort = () => resolve(undefined);
      }),
  );
}

export async function getCachedMedia(key: string): Promise<Blob | null> {
  const entry = await run<Entry | undefined>('readonly', (s) => s.get(key));
  if (!entry || Date.now() - entry.savedAt > MAX_AGE) return null;
  void run('readwrite', (s) => s.put({ ...entry, usedAt: Date.now() }));
  return entry.blob;
}

export async function putCachedMedia(key: string, blob: Blob) {
  await run('readwrite', (s) => s.put({ key, blob, savedAt: Date.now(), usedAt: Date.now() } satisfies Entry));
  const all = ((await run<Entry[]>('readonly', (s) => s.getAll())) ?? []).sort((a, b) => b.usedAt - a.usedAt);
  let bytes = 0;
  const drop = all.filter((e, i) => {
    bytes += e.blob.size;
    return e.key !== key && (i >= MAX_ENTRIES || bytes > MAX_BYTES);
  });
  if (drop.length) await run('readwrite', (s) => drop.forEach((e) => s.delete(e.key)));
}
